import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createClient, updateClient, getClient, getUsers, getFamilies, addFamilyMember, createFamily, parseClientScreenshot } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { formatApiError, isMinor } from '../../lib/utils';
import { 
  CLIENT_STATUSES, CLIENT_TYPES, TAX_STATUSES, KYC_ACQUISITION_TYPES, KYC_STATUSES, 
  GENDERS, PROFESSIONS, INDIAN_STATES, AUM_RANGES, GUARDIAN_RELATIONSHIPS, MEMBER_RELATIONSHIPS 
} from '../../lib/constants';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { DatePicker } from '../../components/ui/date-picker';
import { toast } from 'sonner';
import { ArrowLeft, Camera } from '@phosphor-icons/react';

const isEntityType = (type) => ['HUF', 'Firm', 'Company', 'Trust'].includes(type);

export default function ClientFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [families, setFamilies] = useState([]);
  const [mapToFamily, setMapToFamily] = useState(false);
  const [selectedFamilyId, setSelectedFamilyId] = useState('');
  const [relationshipToHead, setRelationshipToHead] = useState('');
  const [copyAddress, setCopyAddress] = useState(false);
  const [sameAsCallingNumber, setSameAsCallingNumber] = useState(true);
  const [showFamilyAumModal, setShowFamilyAumModal] = useState(false);
  const [familyAum, setFamilyAum] = useState('');
  const [pendingClientId, setPendingClientId] = useState(null);
  const [parsing, setParsing] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    client_type: 'Individual',
    full_name: '',
    email: '',
    phone: '',
    secondary_phone: '',
    pan_number: '',
    aadhaar_number: '',
    date_of_birth: '',
    gender: 'Male',
    profession: '',
    address: '',
    city: '',
    state: 'Punjab',
    pin_code: '',
    client_status: 'Active',
    tax_status: 'Resident Individual',
    kyc_acquisition_type: 'Old KYC',
    kyc_status: 'Validated',
    aum_range: '',
    guardian_pan: '',
    guardian_name: '',
    guardian_relationship: '',
    // Entity fields
    entity_name: '',
    incorporation_date: '',
    registration_number: '',
    karta_name: '',
    authorized_signatory: '',
    // Tracking
    last_portfolio_reviewed_on: '',
    account_managers: [],
    operations_managers: [],
    senior_account_manager: '',
    senior_operations_manager: '',
    // NRI fields
    country_code: '+91',
    foreign_address: '',
    foreign_city: '',
    foreign_country: '',
    foreign_zip: ''
  });

  const clientIsMinor = formData.client_type === 'Minor';
  const isEntity = isEntityType(formData.client_type);
  const isNRI = formData.client_type === 'NRI';
  const requiresFullInfo = !['Inactive', 'Deceased'].includes(formData.client_status);

  // When client is minor, auto-enable family mapping
  useEffect(() => {
    if (clientIsMinor && !isEdit) {
      setMapToFamily(true);
    }
  }, [clientIsMinor, isEdit]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersRes, familiesRes] = await Promise.all([
          getUsers(),
          getFamilies({ include_details: true })
        ]);
        setUsers(usersRes.data);
        setFamilies(familiesRes.data);

        if (isEdit) {
          const clientRes = await getClient(id);
          setFormData(prev => ({
            ...prev,
            ...clientRes.data,
            account_managers: clientRes.data.account_managers || [],
            operations_managers: clientRes.data.operations_managers || [],
            senior_account_manager: clientRes.data.senior_account_manager || ''
          }));
          const cd = clientRes.data;
          setSameAsCallingNumber(!cd.secondary_phone || cd.phone === cd.secondary_phone);
        } else {
          setFormData(prev => ({ ...prev, account_managers: [user?.id] }));
        }
      } catch (error) {
        toast.error(formatApiError(error));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, isEdit, user]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const selectedFamily = families.find(f => f.id === selectedFamilyId);
  const headClient = selectedFamily?.head_client;

  // Build guardian candidates from selected family
  const getFamilyMembers = () => {
    if (!selectedFamily) return [];
    const members = [];
    if (selectedFamily.head_client) {
      members.push({ ...selectedFamily.head_client, _role: 'Head' });
    }
    for (const m of selectedFamily.members || []) {
      if (m.client) {
        members.push({ ...m.client, _role: m.relationship_to_head });
      }
    }
    return members;
  };

  const familyMembers = getFamilyMembers();

  const handleGuardianSelect = (clientId) => {
    const member = familyMembers.find(m => m.id === clientId);
    if (!member) return;
    // Auto-set relationship based on gender
    let autoRelationship = '';
    if (member.gender === 'Female') autoRelationship = 'Mother';
    else if (member.gender === 'Male') autoRelationship = 'Father';

    // Auto-populate address from guardian (domestic + foreign for NRI guardians)
    const guardianIsNRI = member.client_type === 'NRI';
    // If guardian is NRI and has a foreign address, use foreign address as Minor's primary address
    const useForeignAsAddress = guardianIsNRI && member.foreign_address;
    setFormData(prev => ({
      ...prev,
      guardian_pan: member.pan_number || '',
      guardian_name: member.full_name || '',
      guardian_relationship: autoRelationship || prev.guardian_relationship,
      email: member.email || '',
      phone: member.phone || '',
      secondary_phone: member.secondary_phone || member.phone || '',
      // Address: prefer guardian's foreign address if NRI, else domestic
      address: useForeignAsAddress ? member.foreign_address : (member.address || ''),
      city: useForeignAsAddress ? (member.foreign_city || '') : (member.city || ''),
      state: useForeignAsAddress ? '' : (member.state || ''),
      pin_code: useForeignAsAddress ? (member.foreign_zip || '') : (member.pin_code || ''),
      // KYC fields from guardian
      kyc_acquisition_type: member.kyc_acquisition_type || prev.kyc_acquisition_type,
      kyc_status: member.kyc_status || prev.kyc_status,
      // NRI fields from guardian (if guardian is NRI)
      country_code: guardianIsNRI ? (member.country_code || '+91') : prev.country_code,
      foreign_address: guardianIsNRI ? (member.foreign_address || '') : prev.foreign_address,
      foreign_city: guardianIsNRI ? (member.foreign_city || '') : prev.foreign_city,
      foreign_country: guardianIsNRI ? (member.foreign_country || '') : prev.foreign_country,
      foreign_zip: guardianIsNRI ? (member.foreign_zip || '') : prev.foreign_zip,
    }));
    setSameAsCallingNumber(
      !member.secondary_phone || member.phone === member.secondary_phone
    );
  };

  const handleCopyAddress = (checked) => {
    setCopyAddress(checked);
    if (checked && headClient) {
      setFormData(prev => ({
        ...prev,
        address: headClient.address || '',
        city: headClient.city || '',
        state: headClient.state || '',
        pin_code: headClient.pin_code || ''
      }));
    }
  };

  const handleFamilyToggle = (checked) => {
    setMapToFamily(checked);
    if (!checked) {
      setSelectedFamilyId('');
      setRelationshipToHead('');
      setCopyAddress(false);
    }
  };

  const validateForm = () => {
    if (!formData.full_name.trim()) { toast.error('Full Name is required'); return false; }
    if (requiresFullInfo && !formData.phone.trim()) { toast.error('Phone number is required'); return false; }
    if (requiresFullInfo && !formData.email.trim()) { toast.error('Email is required'); return false; }
    if (requiresFullInfo && !clientIsMinor && !formData.pan_number.trim()) { toast.error('PAN Number is required'); return false; }
    if (!isEntity && !formData.date_of_birth) { toast.error('Date of Birth is required'); return false; }
    if (!isEntity && formData.date_of_birth) {
      const year = parseInt(formData.date_of_birth.split('-')[0], 10);
      if (year < 1930) { toast.error('Birth year cannot be before 1930'); return false; }
    }
    if (!isNRI && !formData.address.trim()) { toast.error('Address is required'); return false; }
    if (!isNRI && !formData.city.trim()) { toast.error('City is required'); return false; }
    if (!isNRI && !formData.state) { toast.error('State is required'); return false; }
    if (!isNRI && !formData.pin_code.trim()) { toast.error('PIN Code is required'); return false; }
    if (isEntity && !formData.entity_name.trim()) { toast.error('Entity Name is required'); return false; }
    if (clientIsMinor && mapToFamily && !selectedFamilyId) { toast.error('Please select a family for the minor client'); return false; }
    if (clientIsMinor && !formData.guardian_pan) { toast.error('Guardian PAN is required for minor'); return false; }
    return true;
  };

  const handleSubmit = async (e, createFamilyAfter = false) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload = {
        ...formData,
        secondary_phone: sameAsCallingNumber ? formData.phone : formData.secondary_phone,
        guardian_pan: clientIsMinor ? formData.guardian_pan : null,
        guardian_name: clientIsMinor ? formData.guardian_name : null,
        guardian_relationship: clientIsMinor ? formData.guardian_relationship : null,
        entity_name: isEntity ? formData.entity_name : null,
        incorporation_date: isEntity ? formData.incorporation_date : null,
        registration_number: isEntity ? formData.registration_number : null,
        karta_name: formData.client_type === 'HUF' ? formData.karta_name : null,
        authorized_signatory: isEntity ? formData.authorized_signatory : null,
        date_of_birth: !isEntity ? formData.date_of_birth : null,
        gender: !isEntity ? formData.gender : null,
        profession: clientIsMinor ? null : formData.profession,
        country_code: isNRI ? formData.country_code : '+91',
        foreign_address: isNRI ? formData.foreign_address : null,
        foreign_city: isNRI ? formData.foreign_city : null,
        foreign_country: isNRI ? formData.foreign_country : null,
        foreign_zip: isNRI ? formData.foreign_zip : null,
      };

      if (isEdit) {
        await updateClient(id, payload);
        toast.success('Client updated successfully');
        navigate('/clients');
      } else {
        const res = await createClient(payload);
        // Add to family if selected
        if (mapToFamily && selectedFamilyId && relationshipToHead) {
          try {
            await addFamilyMember(selectedFamilyId, {
              client_id: res.data.id,
              relationship_to_head: relationshipToHead
            });
          } catch (famErr) {
            toast.error('Client created but failed to add to family: ' + formatApiError(famErr));
          }
        }
        // Create new family with this client as head - show AUM modal
        if (createFamilyAfter && !mapToFamily) {
          setPendingClientId(res.data.id);
          setShowFamilyAumModal(true);
          setSaving(false);
          return; // Don't navigate yet
        }
        toast.success('Client created successfully');
        navigate('/clients');
      }
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateFamilyWithAum = async () => {
    if (!pendingClientId) return;
    setSaving(true);
    try {
      await createFamily({
        name: formData.full_name,
        head_client_id: pendingClientId,
        description: familyAum ? `Family AUM: ${familyAum}` : undefined,
        family_aum: familyAum || undefined,
        account_manager: formData.account_managers?.[0] || undefined,
        operations_manager: formData.operations_managers?.[0] || undefined,
        senior_account_manager: formData.senior_account_manager || undefined,
        senior_operations_manager: formData.senior_operations_manager || undefined,
      });
      toast.success('Client created & family created');
      navigate('/clients');
    } catch (error) {
      toast.error('Client created but failed to create family: ' + formatApiError(error));
      navigate('/clients');
    } finally {
      setSaving(false);
      setShowFamilyAumModal(false);
    }
  };

  const handleScreenshotUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input
    e.target.value = '';

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file (PNG, JPG, etc.)');
      return;
    }

    setParsing(true);
    try {
      const reader = new FileReader();
      const base64 = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const res = await parseClientScreenshot(base64);
      const parsed = res.data;

      // Auto-fill form with parsed data
      setFormData(prev => ({
        ...prev,
        full_name: parsed.full_name || prev.full_name,
        pan_number: parsed.pan_number || prev.pan_number,
        email: parsed.email || prev.email,
        date_of_birth: parsed.date_of_birth || prev.date_of_birth,
        phone: parsed.phone || prev.phone,
        secondary_phone: parsed.secondary_phone || prev.secondary_phone,
        address: parsed.address || prev.address,
        city: parsed.city || prev.city,
        state: parsed.state || prev.state,
        pin_code: parsed.pin_code || prev.pin_code,
        gender: parsed.gender || prev.gender,
        profession: parsed.profession || prev.profession,
        company_name: parsed.company_name || prev.company_name,
        bank_name: parsed.bank_name || prev.bank_name,
        account_number: parsed.account_number || prev.account_number,
        ifsc_code: parsed.ifsc_code || prev.ifsc_code,
        aum_range: parsed.aum_range || prev.aum_range,
        tax_status: parsed.tax_status || prev.tax_status,
      }));

      if (parsed.phone && parsed.secondary_phone && parsed.phone === parsed.secondary_phone) {
        setSameAsCallingNumber(true);
      } else if (parsed.secondary_phone) {
        setSameAsCallingNumber(false);
      }

      const filledFields = Object.entries(parsed).filter(([, v]) => v).length;
      toast.success(`Parsed ${filledFields} fields from screenshot`);
    } catch (error) {
      toast.error('Failed to parse screenshot: ' + formatApiError(error));
    } finally {
      setParsing(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[600px]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="client-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/clients')} className="p-2">
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            {isEdit ? 'Edit Client' : 'New Client'}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-4xl">
        {/* Client Type - FIRST SECTION */}
        <Card className="border-zinc-200 rounded-sm mb-6 border-[#002FA7]/30 bg-blue-50/20">
          <CardHeader className="border-b border-[#002FA7]/10 flex flex-row items-center justify-between">
            <CardTitle className="text-base text-[#002FA7]">Client Type</CardTitle>
            {!isEdit && (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleScreenshotUpload}
                  data-testid="screenshot-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={parsing}
                  onClick={() => fileInputRef.current?.click()}
                  className="rounded-sm text-xs gap-1.5 border-[#002FA7]/30 text-[#002FA7] hover:bg-[#002FA7]/5"
                  data-testid="parse-screenshot-button"
                >
                  <Camera size={14} />
                  {parsing ? 'Parsing...' : 'Fill from Screenshot'}
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                Select Client Type <span className="text-red-500">*</span>
              </Label>
              <SearchableSelect
                value={formData.client_type}
                onValueChange={(v) => handleChange('client_type', v)}
                options={CLIENT_TYPES.map(t => ({ value: t.value, label: `${t.label} — ${t.description}` }))}
                placeholder="Select type"
                data-testid="client-type-select"
              />
            </div>
          </CardContent>
        </Card>

        {/* For Minor: Family Mapping + Guardian come FIRST */}
        {clientIsMinor && !isEdit && (
          <>
            {/* Family Mapping - Required for Minor */}
            <Card className="border-zinc-200 rounded-sm mb-6 border-purple-200 bg-purple-50/20">
              <CardHeader className="border-b border-purple-100">
                <CardTitle className="text-base text-purple-700">Step 1: Select Family</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <p className="text-sm text-zinc-500 mb-2">Minor clients must be mapped to an existing family. Select the family this minor belongs to.</p>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                        Select Family <span className="text-red-500">*</span>
                      </Label>
                      <SearchableSelect
                        value={selectedFamilyId || ''}
                        onValueChange={(v) => { setSelectedFamilyId(v); setCopyAddress(false); }}
                        options={families.map(f => ({ value: f.id, label: `${f.name} ${f.head_client ? `(Head: ${f.head_client.full_name})` : ''}` }))}
                        placeholder="Select a family"
                        searchPlaceholder="Search families..."
                        data-testid="family-select"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                        Relationship to Head <span className="text-red-500">*</span>
                      </Label>
                      <SearchableSelect
                        value={relationshipToHead || ''}
                        onValueChange={(v) => setRelationshipToHead(v)}
                        options={MEMBER_RELATIONSHIPS}
                        placeholder="Select relationship"
                        data-testid="relationship-select"
                      />
                    </div>
                  </div>
                  {selectedFamilyId && headClient && (
                    <div className="bg-zinc-50 p-3 rounded-sm text-sm space-y-1">
                      <p className="font-medium">Family Head: {headClient.full_name}</p>
                      {headClient.address && (
                        <p className="text-zinc-500 text-xs">{headClient.address}, {headClient.city}, {headClient.state} - {headClient.pin_code}</p>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Guardian Selection */}
            <Card className="border-zinc-200 rounded-sm mb-6 border-purple-200 bg-purple-50/30">
              <CardHeader className="border-b border-purple-100">
                <CardTitle className="text-base text-purple-700">Step 2: Select Guardian</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {!selectedFamilyId && (
                  <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-sm p-3" data-testid="minor-family-warning">
                    Please select a family above to pick a guardian from family members.
                  </div>
                )}
                {selectedFamilyId && familyMembers.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                      Select Guardian from Family <span className="text-red-500">*</span>
                    </Label>
                    <SearchableSelect
                      value=""
                      onValueChange={handleGuardianSelect}
                      options={familyMembers.map(m => ({ value: m.id, label: `${m.full_name} (${m._role})${m.pan_number ? ` — PAN: ${m.pan_number}` : ''}` }))}
                      placeholder="Select guardian"
                      searchPlaceholder="Search members..."
                      data-testid="guardian-select"
                    />
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                      Guardian PAN <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      value={formData.guardian_pan}
                      onChange={(e) => handleChange('guardian_pan', e.target.value.toUpperCase())}
                      required
                      pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                      className="rounded-sm bg-zinc-50"
                      readOnly={!!selectedFamilyId}
                      data-testid="guardian-pan-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                      Guardian Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      value={formData.guardian_name}
                      onChange={(e) => handleChange('guardian_name', e.target.value)}
                      required
                      className="rounded-sm bg-zinc-50"
                      readOnly={!!selectedFamilyId}
                      data-testid="guardian-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                      Relationship <span className="text-red-500">*</span>
                    </Label>
                    <SearchableSelect
                      value={formData.guardian_relationship}
                      onValueChange={(v) => handleChange('guardian_relationship', v)}
                      options={GUARDIAN_RELATIONSHIPS}
                      placeholder="Select"
                      data-testid="guardian-relationship-select"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Entity/Incorporation Details (for HUF, Firm, Company, Trust) */}
        {isEntity && (
          <Card className="border-zinc-200 rounded-sm mb-6 border-amber-200 bg-amber-50/30">
            <CardHeader className="border-b border-amber-100">
              <CardTitle className="text-base text-amber-700">
                {formData.client_type === 'HUF' ? 'HUF Details' : `${formData.client_type} Details`}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                    Entity Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={formData.entity_name}
                    onChange={(e) => handleChange('entity_name', e.target.value)}
                    required
                    placeholder={formData.client_type === 'HUF' ? 'e.g., Sharma HUF' : 'Registered entity name'}
                    className="rounded-sm"
                    data-testid="entity-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                    Date of Incorporation <span className="text-red-500">*</span>
                  </Label>
                  <DatePicker
                    value={formData.incorporation_date}
                    onChange={(val) => handleChange('incorporation_date', val)}
                    maxDate={new Date().toISOString().split('T')[0]}
                    placeholder="Select incorporation date"
                    data-testid="incorporation-date-input"
                  />
                </div>
                {formData.client_type === 'HUF' && (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                      Karta Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      value={formData.karta_name}
                      onChange={(e) => handleChange('karta_name', e.target.value)}
                      required
                      className="rounded-sm"
                      data-testid="karta-name-input"
                    />
                  </div>
                )}
                {formData.client_type !== 'HUF' && (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                      Registration Number
                    </Label>
                    <Input
                      value={formData.registration_number}
                      onChange={(e) => handleChange('registration_number', e.target.value)}
                      className="rounded-sm"
                      data-testid="registration-number-input"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                    Authorized Signatory <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={formData.authorized_signatory}
                    onChange={(e) => handleChange('authorized_signatory', e.target.value)}
                    required
                    className="rounded-sm"
                    data-testid="authorized-signatory-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Basic Information */}
        <Card className="border-zinc-200 rounded-sm mb-6">
          <CardHeader className="border-b border-zinc-100">
            <CardTitle className="text-base">{isEntity ? 'Contact Information' : 'Personal Information'}</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                  {isEntity ? 'Contact Person Name' : 'Full Name'} <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => handleChange('full_name', e.target.value)}
                  required
                  className="rounded-sm"
                  data-testid="client-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                  Calling Number {requiresFullInfo && <span className="text-red-500">*</span>}
                </Label>
                <div className="flex gap-2">
                  {isNRI && (
                    <Input
                      value={formData.country_code}
                      onChange={(e) => handleChange('country_code', e.target.value)}
                      placeholder="+91"
                      className="rounded-sm w-[80px] flex-shrink-0"
                      data-testid="country-code-input"
                    />
                  )}
                  <Input
                    value={formData.phone}
                    onChange={(e) => {
                      handleChange('phone', e.target.value);
                      if (sameAsCallingNumber) handleChange('secondary_phone', e.target.value);
                    }}
                    required={requiresFullInfo}
                    pattern={isNRI ? "[0-9]{4,15}" : "[6-9][0-9]{9}"}
                    className="rounded-sm"
                    data-testid="client-phone-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                    WhatsApp Number
                  </Label>
                  <div className="flex items-center gap-1.5">
                    <Checkbox
                      id="same-as-calling"
                      checked={sameAsCallingNumber}
                      onCheckedChange={(checked) => {
                        setSameAsCallingNumber(checked);
                        if (checked) handleChange('secondary_phone', formData.phone);
                      }}
                      data-testid="same-as-calling-checkbox"
                    />
                    <label htmlFor="same-as-calling" className="text-[10px] text-zinc-400 cursor-pointer">Same as Calling</label>
                  </div>
                </div>
                <Input
                  value={formData.secondary_phone}
                  onChange={(e) => handleChange('secondary_phone', e.target.value)}
                  disabled={sameAsCallingNumber}
                  className="rounded-sm"
                  data-testid="client-secondary-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                  Email {requiresFullInfo && <span className="text-red-500">*</span>}
                </Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required={requiresFullInfo}
                  className="rounded-sm"
                  data-testid="client-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                  PAN Number {requiresFullInfo && !clientIsMinor && <span className="text-red-500">*</span>}
                </Label>
                <Input
                  value={formData.pan_number}
                  onChange={(e) => handleChange('pan_number', e.target.value.toUpperCase())}
                  required={requiresFullInfo && !clientIsMinor}
                  pattern={formData.pan_number ? "[A-Z]{5}[0-9]{4}[A-Z]{1}" : undefined}
                  title="Enter valid PAN (e.g., ABCDE1234F)"
                  placeholder={clientIsMinor ? "Optional for minor" : ""}
                  className="rounded-sm"
                  data-testid="client-pan-input"
                />
              </div>
              {!isEntity && (
                <>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                      Date of Birth <span className="text-red-500">*</span>
                    </Label>
                    <DatePicker
                      value={formData.date_of_birth}
                      onChange={(val) => {
                        if (val) {
                          const year = parseInt(val.split('-')[0], 10);
                          if (year < 1930) {
                            toast.error('Birth year cannot be before 1930');
                            return;
                          }
                        }
                        handleChange('date_of_birth', val);
                      }}
                      minDate="1930-01-01"
                      maxDate={new Date().toISOString().split('T')[0]}
                      placeholder="Select date of birth"
                      data-testid="client-dob-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                      Gender <span className="text-red-500">*</span>
                    </Label>
                    <SearchableSelect
                      value={formData.gender}
                      onValueChange={(v) => handleChange('gender', v)}
                      options={GENDERS}
                      placeholder="Select gender"
                      data-testid="client-gender-select"
                    />
                  </div>
                </>
              )}
              {!clientIsMinor && (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                  Profession <span className="text-red-500">*</span>
                </Label>
                <SearchableSelect
                  value={formData.profession}
                  onValueChange={(v) => handleChange('profession', v)}
                  options={PROFESSIONS}
                  placeholder="Select profession"
                />
              </div>
              )}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                  Client Status <span className="text-red-500">*</span>
                </Label>
                <SearchableSelect
                  value={formData.client_status}
                  onValueChange={(v) => handleChange('client_status', v)}
                  options={CLIENT_STATUSES}
                  placeholder="Select status"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Guardian Info (for minors) - only shown here for non-Minor type that becomes minor via DOB */}
        {clientIsMinor && formData.client_type !== 'Minor' && (
          <Card className="border-zinc-200 rounded-sm mb-6 border-purple-200 bg-purple-50/30">
            <CardHeader className="border-b border-purple-100">
              <CardTitle className="text-base text-purple-700">Guardian Information (Minor Client)</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {!selectedFamilyId && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-sm p-3" data-testid="minor-family-warning">
                  Please select a family above to pick a guardian from family members.
                </div>
              )}
              {selectedFamilyId && familyMembers.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                    Select Guardian from Family <span className="text-red-500">*</span>
                  </Label>
                  <SearchableSelect
                    value=""
                    onValueChange={handleGuardianSelect}
                    options={familyMembers.map(m => ({ value: m.id, label: `${m.full_name} (${m._role})${m.pan_number ? ` — PAN: ${m.pan_number}` : ''}` }))}
                    placeholder="Select guardian"
                    searchPlaceholder="Search members..."
                    data-testid="guardian-select"
                  />
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                    Guardian PAN <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={formData.guardian_pan}
                    onChange={(e) => handleChange('guardian_pan', e.target.value.toUpperCase())}
                    required
                    pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                    className="rounded-sm bg-zinc-50"
                    readOnly={!!selectedFamilyId}
                    data-testid="guardian-pan-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                    Guardian Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={formData.guardian_name}
                    onChange={(e) => handleChange('guardian_name', e.target.value)}
                    required
                    className="rounded-sm bg-zinc-50"
                    readOnly={!!selectedFamilyId}
                    data-testid="guardian-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                    Relationship <span className="text-red-500">*</span>
                  </Label>
                    <SearchableSelect
                      value={formData.guardian_relationship}
                      onValueChange={(v) => handleChange('guardian_relationship', v)}
                      options={GUARDIAN_RELATIONSHIPS}
                      placeholder="Select"
                      data-testid="guardian-relationship-select"
                    />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Family Mapping - Only shown for new clients and NOT Minor (Minor has it above) */}
        {!isEdit && !clientIsMinor && (
          <Card className="border-zinc-200 rounded-sm mb-6">
            <CardHeader className="border-b border-zinc-100">
              <CardTitle className="text-base">Family Mapping</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                  Map to Existing Family?
                </Label>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="map-family-yes"
                      checked={mapToFamily}
                      onCheckedChange={handleFamilyToggle}
                      disabled={clientIsMinor}
                      data-testid="map-to-family-checkbox"
                    />
                    <label htmlFor="map-family-yes" className="text-sm cursor-pointer">
                      {clientIsMinor ? 'Yes (required for minor)' : 'Yes'}
                    </label>
                  </div>
                </div>
              </div>

              {mapToFamily && (
                <div className="space-y-4 pt-2 border-t border-zinc-100">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                        Select Family <span className="text-red-500">*</span>
                      </Label>
                      <SearchableSelect
                        value={selectedFamilyId || ''}
                        onValueChange={(v) => { setSelectedFamilyId(v); setCopyAddress(false); }}
                        options={families.map(f => ({ value: f.id, label: `${f.name} ${f.head_client ? `(Head: ${f.head_client.full_name})` : ''}` }))}
                        placeholder="Select a family"
                        searchPlaceholder="Search families..."
                        data-testid="family-select"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                        Relationship to Head <span className="text-red-500">*</span>
                      </Label>
                      <SearchableSelect
                        value={relationshipToHead || ''}
                        onValueChange={(v) => setRelationshipToHead(v)}
                        options={MEMBER_RELATIONSHIPS}
                        placeholder="Select relationship"
                        data-testid="relationship-select"
                      />
                    </div>
                  </div>

                  {selectedFamilyId && headClient && (
                    <div className="bg-zinc-50 p-3 rounded-sm text-sm space-y-1">
                      <p className="font-medium">Family Head: {headClient.full_name}</p>
                      {headClient.address && (
                        <p className="text-zinc-500 text-xs">{headClient.address}, {headClient.city}, {headClient.state} - {headClient.pin_code}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Address */}
        <Card className="border-zinc-200 rounded-sm mb-6">
          <CardHeader className="border-b border-zinc-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{isNRI ? 'Indian Address (Optional)' : 'Address'}</CardTitle>
              {mapToFamily && selectedFamilyId && headClient?.address && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="copy-address"
                    checked={copyAddress}
                    onCheckedChange={handleCopyAddress}
                    data-testid="copy-address-checkbox"
                  />
                  <label htmlFor="copy-address" className="text-sm cursor-pointer text-zinc-600">Copy from Family Head</label>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {isNRI && (
              <p className="text-xs text-zinc-400">Indian address is optional for NRI clients. You can provide either an Indian address, a foreign address, or both.</p>
            )}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                Address {!isNRI && <span className="text-red-500">*</span>}
              </Label>
              <Input
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                required={!isNRI}
                className="rounded-sm"
                data-testid="client-address-input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                  City {!isNRI && <span className="text-red-500">*</span>}
                </Label>
                <Input
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  required={!isNRI}
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                  State {!isNRI && <span className="text-red-500">*</span>}
                </Label>
                <SearchableSelect
                  value={formData.state}
                  onValueChange={(v) => handleChange('state', v)}
                  options={INDIAN_STATES}
                  placeholder="Select state"
                  searchPlaceholder="Search states..."
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                  PIN Code {!isNRI && <span className="text-red-500">*</span>}
                </Label>
                <Input
                  value={formData.pin_code}
                  onChange={(e) => handleChange('pin_code', e.target.value)}
                  required={!isNRI}
                  pattern="[1-9][0-9]{5}"
                  className="rounded-sm"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Foreign Address (NRI only) */}
        {isNRI && (
          <Card className="border-zinc-200 rounded-sm mb-6 border-teal-200 bg-teal-50/20">
            <CardHeader className="border-b border-teal-100">
              <CardTitle className="text-base text-teal-700">Foreign Address</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                  Foreign Address
                </Label>
                <Input
                  value={formData.foreign_address}
                  onChange={(e) => handleChange('foreign_address', e.target.value)}
                  placeholder="Street address, apartment, etc."
                  className="rounded-sm"
                  data-testid="foreign-address-input"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                    City
                  </Label>
                  <Input
                    value={formData.foreign_city}
                    onChange={(e) => handleChange('foreign_city', e.target.value)}
                    className="rounded-sm"
                    data-testid="foreign-city-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                    Country
                  </Label>
                  <Input
                    value={formData.foreign_country}
                    onChange={(e) => handleChange('foreign_country', e.target.value)}
                    className="rounded-sm"
                    data-testid="foreign-country-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                    ZIP / Postal Code
                  </Label>
                  <Input
                    value={formData.foreign_zip}
                    onChange={(e) => handleChange('foreign_zip', e.target.value)}
                    className="rounded-sm"
                    data-testid="foreign-zip-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* KYC & Tax Info */}
        <Card className="border-zinc-200 rounded-sm mb-6">
          <CardHeader className="border-b border-zinc-100">
            <CardTitle className="text-base">KYC & Tax Information</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                  Tax Status <span className="text-red-500">*</span>
                </Label>
                <SearchableSelect
                  value={formData.tax_status}
                  onValueChange={(v) => handleChange('tax_status', v)}
                  options={TAX_STATUSES}
                  placeholder="Select"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                  KYC Type <span className="text-red-500">*</span>
                </Label>
                <SearchableSelect
                  value={formData.kyc_acquisition_type}
                  onValueChange={(v) => handleChange('kyc_acquisition_type', v)}
                  options={KYC_ACQUISITION_TYPES}
                  placeholder="Select"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                  KYC Status <span className="text-red-500">*</span>
                </Label>
                <SearchableSelect
                  value={formData.kyc_status}
                  onValueChange={(v) => handleChange('kyc_status', v)}
                  options={KYC_STATUSES}
                  placeholder="Select"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                  AUM Range
                </Label>
                <SearchableSelect
                  value={formData.aum_range}
                  onValueChange={(v) => handleChange('aum_range', v)}
                  options={AUM_RANGES}
                  placeholder="Select"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Portfolio & Tracking */}
        <Card className="border-zinc-200 rounded-sm mb-6">
          <CardHeader className="border-b border-zinc-100">
            <CardTitle className="text-base">Portfolio Tracking</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                  Last Portfolio Reviewed On
                </Label>
                <DatePicker
                  value={formData.last_portfolio_reviewed_on || ''}
                  onChange={(val) => handleChange('last_portfolio_reviewed_on', val)}
                  maxDate={new Date().toISOString().split('T')[0]}
                  placeholder="Select review date"
                  data-testid="last-portfolio-reviewed-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Team Assignment */}
        <Card className="border-zinc-200 rounded-sm mb-6">
          <CardHeader className="border-b border-zinc-100">
            <CardTitle className="text-base">Team Assignment</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                  Account Manager <span className="text-red-500">*</span>
                </Label>
                <SearchableSelect
                  value={formData.account_managers?.[0] || ''}
                  onValueChange={(v) => handleChange('account_managers', v ? [v] : [])}
                  options={users.filter(u => u.status === 'active').map(u => ({ value: u.id, label: u.name }))}
                  placeholder="Select AM"
                  data-testid="client-am-select"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                  Senior Account Manager
                </Label>
                <SearchableSelect
                  value={formData.senior_account_manager || ''}
                  onValueChange={(v) => handleChange('senior_account_manager', v || '')}
                  options={[{ value: '', label: 'None' }, ...users.filter(u => u.status === 'active').map(u => ({ value: u.id, label: u.name }))]}
                  placeholder="Select Sr. AM"
                  data-testid="client-sr-am-select"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                  Operations Manager
                </Label>
                <SearchableSelect
                  value={formData.operations_managers?.[0] || ''}
                  onValueChange={(v) => handleChange('operations_managers', v ? [v] : [])}
                  options={[{ value: '', label: 'None' }, ...users.filter(u => u.status === 'active').map(u => ({ value: u.id, label: u.name }))]}
                  placeholder="Select OM"
                  data-testid="client-om-select"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                  Senior Operations Manager
                </Label>
                <SearchableSelect
                  value={formData.senior_operations_manager || ''}
                  onValueChange={(v) => handleChange('senior_operations_manager', v || '')}
                  options={[{ value: '', label: 'None' }, ...users.filter(u => u.status === 'active').map(u => ({ value: u.id, label: u.name }))]}
                  placeholder="Select Sr. OM"
                  data-testid="client-sr-om-select"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/clients')} className="rounded-sm">
            Cancel
          </Button>
          {!isEdit && !mapToFamily && (
            <Button
              type="button"
              disabled={saving}
              onClick={(e) => handleSubmit(e, true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
              data-testid="client-save-create-family-button"
            >
              {saving ? 'Saving...' : 'Save & Create Family'}
            </Button>
          )}
          <Button
            type="submit"
            disabled={saving}
            className="bg-[#002FA7] hover:bg-[#00227A] text-white rounded-sm"
            data-testid="client-submit-button"
          >
            {saving ? 'Saving...' : (isEdit ? 'Update Client' : 'Create Client')}
          </Button>
        </div>
      </form>

      {/* Family AUM Modal */}
      <Dialog open={showFamilyAumModal} onOpenChange={() => {}}>
        <DialogContent className="[&>button]:hidden">
          <DialogHeader>
            <DialogTitle>Family AUM</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-600">
                Family-level AUM (Assets Under Management) <span className="text-red-500">*</span>
              </Label>
              <SearchableSelect
                value={familyAum}
                onValueChange={(v) => setFamilyAum(v)}
                options={AUM_RANGES}
                placeholder="Select AUM range"
                data-testid="family-aum-input"
              />
              <p className="text-xs text-zinc-400">Total assets under management for this family.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowFamilyAumModal(false); navigate('/clients'); }} className="rounded-sm">
              Cancel
            </Button>
            <Button onClick={handleCreateFamilyWithAum} disabled={saving || !familyAum} className="bg-[#002FA7] hover:bg-[#00227A] text-white rounded-sm" data-testid="save-family-aum-button">
              {saving ? 'Creating...' : 'Create Family'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
